import { CommonApiService } from '@services/common-api-service';
import { Component, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import { If } from '@utils/If';
import { z } from 'zod';

/**
 * Zod schema for validating customer details form input.
 * Validates:
 * - name: Required, max 100 chars
 * - email: Required, valid email format, max 100 chars
 * - lastFourDigits: Required, exactly 4 digits
 * - dob: Required, valid date, must be 18+ years old, not in future
 */
const CustomerSchema = z
  .object({
    name: z
      .string()
      .min(3, 'Full name is required.')
      .max(100, 'Name cannot exceed 100 characters.')
      .trim()
      .regex(/^[a-zA-Z\s.'-]+$/, "Name can only contain letters, spaces, and basic punctuation (.'-)"),
    email: z.string().min(1, 'Email is required.').max(100, 'Email cannot exceed 100 characters.').email('Enter a valid email address.').trim(),
    lastFourDigits: z
      .string()
      .min(1, 'Last 4 digits are required.')
      .regex(/^\d{4}$/, 'Enter the valid last 4 digits of your card.')
      .transform(val => val.trim()),
    dob: z.string().min(1, 'Date of birth is required.'),
    mobileno: z.string().min(1, 'Mobile number is required.').max(10, 'Mobile number cannot exceed 10 characters.').regex(/^\d{10}$/, 'Enter a valid 10-digit mobile number.'),
  })
  .superRefine((data, ctx) => {
    const date = new Date(data.dob);

    if (isNaN(date.getTime())) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Invalid date format.'
      });
      return;
    }

    const now = new Date();
    if (date > now) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Date of birth cannot be in the future.'
      });
    }

    const age = now.getFullYear() - date.getFullYear();
    const hadBirthdayThisYear = now.getMonth() > date.getMonth() || (now.getMonth() === date.getMonth() && now.getDate() >= date.getDate());

    const actualAge = hadBirthdayThisYear ? age : age - 1;

    if (actualAge < 18) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'You must be at least 18 years old.'
      });
    }

    if (actualAge > 120) {
      ctx.addIssue({
        path: ['dob'],
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid date of birth.'
      });
    }
  });

type CustomerFormInput = {
  name: string;
  email: string;
  lastFourDigits: string;
  dob: string;
  title: string;
  mobileno: string;
};
@Component({
  tag: 'customer-details',
  styleUrl: 'customer-details.scss',
  shadow: true
})
export class CustomerDetails {
  @Prop() loginId: string = '';
  @Prop() mobileNumber: string = '';
  @Prop() assetPath = '';
  @Prop() loginDetails: any = {};
  @Event({ bubbles: false }) skip: EventEmitter<void>;
  @Event({ bubbles: false }) success: EventEmitter<void>;
  @State() form: CustomerFormInput = {
    name: '',
    email: '',
    lastFourDigits: '',
    dob: '',
    title: 'Mr',
    mobileno: this.mobileNumber
  };
  @State() errors: Record<keyof CustomerFormInput, string> & { generic?: string } = {
    name: '',
    email: '',
    lastFourDigits: '',
    dob: '',
    generic: '',
    title: '',
    mobileno: ''
  };
  @State() touched: Record<keyof CustomerFormInput, boolean> = {
    name: false,
    email: false,
    lastFourDigits: false,
    dob: false,
    title: false,
    mobileno: false
  };
  @State() submitted = false;
  @State() isSubmitting = false;

  private apiService = new CommonApiService();

  private updateField(field: keyof CustomerFormInput, value: string) {
    this.form = { ...this.form, [field]: value };
    this.errors = { ...this.errors, generic: '' };
    if (this.touched[field] || this.submitted) {
      const error = this.validateField(field, value);
      this.errors = { ...this.errors, [field]: error };
    }
  }

  private handleBlur(field: keyof CustomerFormInput) {
    this.touched = { ...this.touched, [field]: true };
    const error = this.validateField(field, this.form[field]);
    this.errors = { ...this.errors, [field]: error };
  }

  private validateField(field: keyof CustomerFormInput, value: string): string {
    const partial = { ...this.form, [field]: value };
    const result = CustomerSchema.safeParse(partial);
    if (!result.success) {
      const issue = result.error.issues.find(i => i.path[0] === field);
      return issue ? issue.message : '';
    }
    return '';
  }

  submitCustDetails = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    this.submitted = true;
    if (this.isSubmitting) return;

    const errors: Record<keyof CustomerFormInput, string> = {
      name: this.validateField('name', this.form.name),
      email: this.validateField('email', this.form.email),
      lastFourDigits: this.validateField('lastFourDigits', this.form.lastFourDigits),
      dob: this.validateField('dob', this.form.dob),
      title: this.validateField('title', this.form.title),
      mobileno: this.validateField('mobileno', this.form.mobileno),
    };
    this.errors = errors;

    if (Object.values(errors).some(e => e)) {
      return;
    }
    this.isSubmitting = true;

    try {
      const body = {
        firstname: this.form.name,
        mobileno: this.mobileNumber,
        email: this.form.email,
        dob: this.form.dob,
        lastFourDigits: this.form.lastFourDigits,
        title: this.form.title,
        customerid: this.loginDetails.customer_id
      };
      const result = await this.apiService.post('/modifyCustomer', body, {}, true);
      if (result.status) {
        this.success.emit({ ...result.eventData, respHeaders: result.headers });
      } else {
        this.errors['generic'] = result['msg'] || 'Oops! Something went wrong. Please try again.';
      }
    } catch (error) {
      this.errors['generic'] = 'We\'re having a tech hiccup. Hang tight!';
    } finally {
      this.isSubmitting = false;
    }
  };


  private get maxDob() {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    // Format as YYYY-MM-DD
    return today.toISOString().split('T')[0];
  }
  skipCustDetails = (event: Event) => {
    event.stopPropagation();
    this.skip.emit();
  };

  render() {
    return (
      <Host>
        <form onSubmit={this.submitCustDetails} novalidate>
          <div class="flex flex-col justify-center gap-5">
            <div class="flex flex-col items-center">
              <h5 class="font-semibold text-black text-2xl">Looks like you're new here</h5>
              <span class="text-gray-600 mt-2 text-sm">Let's get you signed up!</span>
            </div>
            <div class="flex">
              <selection-dropdown onValueChanged={e => this.updateField('title', e.detail)} value={this.form.title} />
              <input-field
                class="flex-grow-1"
                label="Your Full Name"
                name="name"
                maxLength={100}
                restrictInput={true}
                type="text"
                value={this.form.name}
                error={this.errors.name}
                onValueChanged={e => this.updateField('name', e.detail)}
                onBlur={() => this.handleBlur('name')}
              />
            </div>
            <input-field
              label="Email Id"
              name="email"
              type="email"
              value={this.form.email}
              error={this.errors.email}
              onValueChanged={e => this.updateField('email', e.detail)}
              onBlur={() => this.handleBlur('email')}
            />
            <phone-input
              label='Card Mobile No.'
              name='mobileno'
              value={this.form.mobileno}
              onValueChanged={e => this.updateField('mobileno', e.detail.mobile)}
              error={this.errors.mobileno}
              onBlur={() => this.handleBlur('mobileno')}
            />
            <input-field
              label="Last 4 digits of your card"
              name="lastFourDigits"
              type="tel"
              maxLength={4}
              restrictInput={true}
              inputType="number"
              value={this.form.lastFourDigits}
              error={this.errors.lastFourDigits}
              onValueChanged={e => this.updateField('lastFourDigits', e.detail)}
              onBlur={() => this.handleBlur('lastFourDigits')}
            />
            <input-field
              label="Date of Birth"
              name="dob"
              type="date"
              max={this.maxDob}
              value={this.form.dob}
              error={this.errors.dob}
              onValueChanged={e => this.updateField('dob', e.detail)}
              onBlur={() => this.handleBlur('dob')}
            />
            <input type="submit" class="hidden" />
          </div>
          <div class="mt-5">
            <If condition={!!this.errors['generic']} children={<span class="block mb-2 text-center text-sm text-red-400">{this.errors['generic']}</span>} />
            <div class="flex flex-col-reverse md:flex-row gap-4">
              <custom-button onButtonClick={this.skipCustDetails} class="flex-1" variant="outlined" type="button">
                Skip
              </custom-button>
              <custom-button disabled={this.isSubmitting} class="flex-1" type="submit" onButtonClick={this.submitCustDetails}>
                Save & Proceed
              </custom-button>
            </div>
          </div>
        </form>
      </Host>
    );
  }
}
